import { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid, Card, Avatar, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsIcon from "@mui/icons-material/Groups";
import { getEmpId, hasPermission } from "../utils/auth";
import { useCurrentUser } from "../utils/useCurrentUser";
import { IconButton, Tooltip } from "@mui/material";
import { Fab } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const [totalEmp, setTotalEmp] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);

  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  useEffect(() => {
    isMounted.current = true;
    const fetchDashboardData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/dashboard-data`, {
          method: "GET",
          credentials: "include",
        });

        if (res.status === 401 || res.status === 403) {
          const errorData = await res.json();
          throw new Error(errorData.message || "Session Expired");
        }

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        if (isMounted.current) {
          setDashboardData(data);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err.message);
        navigate("/login", { replace: true });
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };
    fetchDashboardData();
    return () => {
      isMounted.current = false;
    };
  }, [navigate]);

  useEffect(() => {
    const fetchLeaveBalance = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/Leave-Balance/${empId}`);
        const data = await response.json();
        setLeaveBalance(data);
      } catch (error) {
        console.error("Error fetching leave balance:", error);
      }
    };
    fetchLeaveBalance();
  }, []);

  // const handleLogout = () => {
  //   localStorage.removeItem("token");
  //   navigate("/login", { replace: true });
  // };
  const handleLogout = async () => {
    try {
      await fetch(`${BASE_URL}/api/logout`, {
        method: "POST",
        credentials: "include", // important to send the HttpOnly cookie
      });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      // Redirect to login
      navigate("/login", { replace: true });
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error || !dashboardData)
    return (
      <Typography color="error">
        {error || "No dashboard data available."}
      </Typography>
    );

  const StatCard = ({ icon, title, value, bgColor }) => (
    <Card
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        bgcolor: bgColor,
        color: "white",
        width: 250,
        height: 70,
      }}
    >
      <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 1 }}>{icon}</Avatar>
      <Box>
        <Typography variant="body2">{title}</Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </Card>
  );

  return (
    <Box sx={{ p: 0, mt: "-20px", ml: "20px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" style={{ fontSize: "25px" }}>
          Dashboard –{" "}
          {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EventAvailableOutlinedIcon />}
          onClick={() => navigate("/employeeattendance")}
          sx={{
            borderRadius: "20px",
            textTransform: "none",
          }}
        >
          Check-In/Out
        </Button>

        {/* <Button variant="contained" color="error" onClick={handleLogout}>
          Logout
        </Button> */}
      </Box>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          p: 2,
          boxShadow: 3,
          background: "linear-gradient(135deg, #f5f7fa, #c3cfe2)",
        }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: "#1976d2",
            fontSize: 28,
            mr: 3,
          }}
        >
          {dashboardData?.email?.[0]?.toUpperCase() || "U"}
        </Avatar>
        <Box>
          <Typography variant="h6" gutterBottom>
            Welcome, {dashboardData.full_name || "User"}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Employee ID:</strong> {dashboardData.emp_id || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Role:</strong> {dashboardData.role || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Organization:</strong> {dashboardData.bo_id || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<WorkIcon />}
            title="Total Projects"
            value={dashboardData.total_projects || 0}
            bgColor="#1976d2"
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<ScheduleIcon />}
            title="Pending Timesheets"
            value={dashboardData.pending_timesheets}
            bgColor="#f57c00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<BeachAccessIcon />}
            title="Leave Balance"
            value={`${leaveBalance.remainingLeaves} days`}
            bgColor="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<CheckCircleIcon />}
            title="Approved Timesheets"
            value={dashboardData.approved_timesheets}
            bgColor="#512da8"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<CalendarTodayIcon />}
            title="Upcoming Holidays"
            value={dashboardData.upcoming_holidays || 0}
            bgColor="#0097a7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
          <StatCard
            icon={<GroupsIcon />}
            title="Total Employees"
            value={dashboardData.total_employees}
            bgColor="#c2185b"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
